import { useDeferredValue, useMemo, useState } from 'react';
import { useArtifactFeed, useAttentionItems, useMembers } from '../hooks/useApi';
import ArtifactList, { type ArtifactListFilters } from '../components/ArtifactList';
import NeedsAttention from '../components/NeedsAttention';
import EmptyState from '../components/EmptyState';

const EMPTY_FILTERS: ArtifactListFilters = {
  name: '',
  ownerId: '',
  type: '',
  createdFrom: '',
  createdTo: '',
  updatedFrom: '',
  updatedTo: '',
};

function toStartOfDay(value: string): string | undefined {
  if (!value) return undefined;
  return `${value}T00:00:00.000Z`;
}

function toEndOfDay(value: string): string | undefined {
  if (!value) return undefined;
  return `${value}T23:59:59.999Z`;
}

export default function Dashboard() {
  const [sort, setSort] = useState<'recent' | 'alphabetical' | 'starred' | 'starred_only'>('recent');
  const [view, setView] = useState<'active' | 'archived'>('active');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<ArtifactListFilters>(EMPTY_FILTERS);
  const deferredSearch = useDeferredValue(search.trim());
  const query = useMemo(
    () => ({
      sort,
      view,
      q: deferredSearch || undefined,
      owner_id: filters.ownerId || undefined,
      type: filters.type || undefined,
      created_from: toStartOfDay(filters.createdFrom),
      created_to: toEndOfDay(filters.createdTo),
      updated_from: toStartOfDay(filters.updatedFrom),
      updated_to: toEndOfDay(filters.updatedTo),
    }),
    [deferredSearch, filters.createdFrom, filters.createdTo, filters.ownerId, filters.type, filters.updatedFrom, filters.updatedTo, sort, view],
  );
  const { artifacts, total, loading, loadingMore, error, hasMore, loadMore, refetch } = useArtifactFeed(query);
  const { items: attentionItems } = useAttentionItems();
  const { members } = useMembers();
  const hasActiveQuery = !!(
    deferredSearch
    || filters.ownerId
    || filters.type
    || filters.createdFrom
    || filters.createdTo
    || filters.updatedFrom
    || filters.updatedTo
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div
          className="w-[32px] h-[32px] border-[3px] rounded-full animate-spin"
          style={{
            borderColor: 'var(--rd-border)',
            borderTopColor: 'var(--rd-text-strong)',
          }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        icon="icon-app-warning"
        title="Could not load artifacts"
        subtitle={error}
      />
    );
  }

  if (artifacts.length === 0) {
    if (view === 'archived' && !hasActiveQuery) {
      return (
        <EmptyState
          icon="icon-app-folder"
          title="No archived artifacts"
          subtitle="Archived artifacts will appear here."
          ctaLabel="Show active artifacts"
          onCtaClick={() => setView('active')}
        />
      );
    }

    if (sort === 'starred_only' && !hasActiveQuery) {
      return (
        <EmptyState
          icon="icon-app-star"
          title="No starred artifacts yet"
          subtitle="Star artifacts to keep them pinned to the top of your workflow."
          ctaLabel="Show all artifacts"
          onCtaClick={() => setSort('recent')}
        />
      );
    }

    if (hasActiveQuery) {
      return (
        <div>
          <NeedsAttention items={attentionItems} />
          <ArtifactList
            artifacts={artifacts}
            total={total}
            members={members}
            view={view}
            onViewChange={setView}
            sort={sort}
            onSortChange={setSort}
            onArtifactsChanged={refetch}
            searchValue={search}
            onSearchChange={(value) => {
              setSearch(value);
              setFilters((current) => ({ ...current, name: value }));
            }}
            filters={filters}
            onFiltersChange={(next) => {
              setFilters(next);
              if (next.name !== search) setSearch(next.name);
            }}
            hasMore={hasMore}
            loadingMore={loadingMore}
            onLoadMore={loadMore}
          />
          <EmptyState
            icon="icon-app-search"
            title="No artifacts match those filters"
            subtitle="Try a different name, owner, type, or date range."
            ctaLabel="Clear filters"
            onCtaClick={() => {
              setSearch('');
              setFilters(EMPTY_FILTERS);
              setSort('recent');
            }}
          />
        </div>
      );
    }

    return (
      <EmptyState
        icon="icon-app-download"
        title="Publish your first artifact"
        subtitle="Share interactive content from Claude with your team"
        ctaLabel="Set up Claude connection"
        onCtaClick={() => {
          window.location.href = '/settings';
        }}
      />
    );
  }

  return (
    <div className="pb-[var(--sp-6)]">
      <NeedsAttention items={attentionItems} />
      <ArtifactList
        artifacts={artifacts}
        total={total}
        members={members}
        view={view}
        onViewChange={setView}
        sort={sort}
        onSortChange={setSort}
        onArtifactsChanged={refetch}
        searchValue={search}
        onSearchChange={(value) => {
          setSearch(value);
          setFilters((current) => ({ ...current, name: value }));
        }}
        filters={filters}
        onFiltersChange={(next) => {
          setFilters(next);
          if (next.name !== search) setSearch(next.name);
        }}
        hasMore={hasMore}
        loadingMore={loadingMore}
        onLoadMore={loadMore}
      />
    </div>
  );
}
